import {DateTime} from 'luxon';
import {DateTimeOptions} from 'luxon/src/datetime';

const MINIMUM_PAST_DATE = '1900-01-01';
const DEFAULT_FORMAT_VALUE = 'MM/dd/yyyy';

export function addDays(date: MsApp.DateTimeString, daysToAdd: number, dateFormat = DEFAULT_FORMAT_VALUE): string {
  const dateTime = DateTime.fromFormat(date, dateFormat).plus({days: daysToAdd});
  return dateTime.toFormat(dateFormat);
}

export function isDateValid(val: MsApp.DateString): boolean {
  return !!(val && parseDateTime(val).isValid && parseDateTime(val) >= DateTime.fromISO(MINIMUM_PAST_DATE));
}

export function isAfter(val1: MsApp.DateString | DateTime, val2: MsApp.DateString | DateTime, needsParsing = true) {
  return needsParsing ? parseDateTime(val1) > parseDateTime(val2) : val1 > val2;
}

export function isDateIsToday(val: MsApp.DateString) {
  return DateTime.now().hasSame(parseDateTime(val), 'day');
}

export function formatDateInUTC(date: string, formatValue = DEFAULT_FORMAT_VALUE): string {
  return parseDateTime(date, {zone: 'utc'}).toFormat(formatValue);
}

export function formatDateWithoutUTC(date: string, formatValue = DEFAULT_FORMAT_VALUE): string {
  return parseDateTime(date).toFormat(formatValue);
}

// @TODO: This is not used
// method for converting date from UTC to Central Time
export function formatDateToCST(date: string, formatValue = 'MMM dd yyyy HH:mm A'): string {
  return parseDateTime(date, {zone: 'America/Chicago'}).toFormat(formatValue);
}

export function isFutureDate(val: string) {
  return parseDateTime(val).startOf('day') > DateTime.now().startOf('day');
}

export function isFutureDateTodayIncluded(val: string) {
  return parseDateTime(val).startOf('day') >= DateTime.now().startOf('day');
}

export function isSameOrFutureMonth(val: string) {
  return parseDateTime(val).startOf('month') >= DateTime.now().startOf('month');
}

// @TODO: This needs better name - a date in the future will return true
export function isPastDate(val: string) {
  return parseDateTime(val) >= DateTime.fromISO(MINIMUM_PAST_DATE);
}

function parseStringDateTime(val?: any, options?: DateTimeOptions): DateTime {
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(val)) {
    return DateTime.fromFormat(val, 'MM/dd/yyyy', options);
  }

  if (/^\d{2}\/\d{4}$/.test(val)) {
    return DateTime.fromFormat(val, 'MM/yyyy', options);
  }

  if (/^\d{2}\/\d{2}$/.test(val)) {
    return DateTime.fromFormat(val, 'MM/dd', options);
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
    return DateTime.fromFormat(val, 'yyyy-MM-dd', options);
  }

  if (/^\d{4}-\d{2}$/.test(val)) {
    return DateTime.fromFormat(val, 'yyyy-MM', options);
  }

  if (/^\d{2}-\d{2}$/.test(val)) {
    return DateTime.fromFormat(val, 'MM-dd', options);
  }

  if (/^(19[0-9][0-9]|20[0-9][0-9])$/.test(val)) {
    return DateTime.fromFormat(val, 'yyyy', options);
  }

  return DateTime.fromJSDate(new Date(val), options);
}

export function parseDateTime(val?: any, options?: DateTimeOptions): DateTime {
  if (val === undefined) {
    return DateTime.now();
  }

  if (!val) {
    return DateTime.fromISO(null);
  }

  if (val instanceof Date) {
    return DateTime.fromJSDate(val, options);
  }

  if (val instanceof DateTime) {
    return DateTime.fromISO(val.toISO(), options);
  }

  if (val instanceof String) {
    val = val.toString();
  }

  if (typeof val === 'string') {
    return parseStringDateTime(val, options);
  }

  if (Number.isFinite(val)) {
    return DateTime.fromMillis(val, options);
  }

  return DateTime.fromISO(null);
}

export function getDifferenceInMonth(from: string, to: string | number) {
  return Math.floor(parseDateTime(to).diff(parseDateTime(from), 'months').months);
}

export const getAgeInMonth = (value: string) =>
  !!value && parseDateTime(value).isValid && !isFutureDate(value) && isPastDate(value)
    ? getDifferenceInMonth(value, Date.now())
    : null;

export const getAge = (value: string) => Math.floor((getAgeInMonth(value) || 0) / 12);

export function isDateFormatValid(val: MsApp.DateString, format: string) {
  return (
    DateTime.fromFormat(val, format).isValid && DateTime.fromFormat(val, format) >= DateTime.fromISO(MINIMUM_PAST_DATE)
  );
}
